import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Divider,
  FormControlLabel, Grid, IconButton, InputAdornment, Skeleton, Stack,
  Switch, TextField, Typography
} from '@mui/material';
import Swal from 'sweetalert2';
import Icon from '../../../components/icon';
import api from '../../../utils/axiosConfig';
import { emptyStep, ProsesProduksi, ProsesStep } from './types';
import './proses-produksi.scss';

const defaultStepNames = ['Gudang', 'Printing', 'Potong', 'Bordir', 'Pembelian', 'BHP'];
const defaultSteps = (): ProsesStep[] => defaultStepNames.map((nama_step, index) => ({
  ...emptyStep(index + 1),
  nama_step,
}));
const isBhpStep = (step: ProsesStep) => step.nama_step.trim().toUpperCase() === 'BHP';

const initialValue: ProsesProduksi = {
  nama_proses: '', keterangan: '', is_aktif: true, steps: defaultSteps()
};

const errorMessage = (error: any) =>
  error?.response?.data?.message || error?.message || 'Terjadi kesalahan. Silakan coba lagi.';

const unwrapDetail = (payload: any): ProsesProduksi => {
  const value = payload?.data?.data ?? payload?.data ?? payload;
  return {
    ...initialValue,
    ...value,
    is_aktif: Boolean(value?.is_aktif),
    steps: (value?.steps || value?.detail || []).map((step: ProsesStep, index: number) => ({
      ...step,
      urutan: step.urutan || index + 1,
      harga_borongan: Number(step.harga_borongan || 0),
    })),
  };
};

export default function ProsesProduksiForm({ mode }: { mode: 'add' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState<ProsesProduksi>(initialValue);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    const controller = new AbortController();
    api.get(`/production/proses-borongan/detail/${id}`, { signal: controller.signal })
      .then(({ data }) => setForm(unwrapDetail(data)))
      .catch(err => {
        if (err?.code !== 'ERR_CANCELED') setError(errorMessage(err));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [id, mode]);

  const setField = <K extends keyof ProsesProduksi>(key: K, value: ProsesProduksi[K]) =>
    setForm(current => ({ ...current, [key]: value }));

  const setStep = (index: number, key: keyof ProsesStep, value: any) => {
    setForm(current => ({
      ...current,
      steps: current.steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, [key]: value } : step
      ),
    }));
  };

  const addStep = () => setForm(current => {
    const regularSteps = current.steps.filter(step => !isBhpStep(step));
    const bhpStep = current.steps.find(isBhpStep) || { ...emptyStep(1), nama_step: 'BHP' };
    const steps = [...regularSteps, emptyStep(regularSteps.length + 1), bhpStep]
      .map((step, index) => ({ ...step, urutan: index + 1 }));
    return { ...current, steps };
  });

  const removeStep = (index: number) => setForm(current => {
    if (isBhpStep(current.steps[index])) return current;
    return {
      ...current,
      steps: current.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, urutan: i + 1 })),
    };
  });

  const validate = () => {
    if (!form.nama_proses.trim()) return 'Nama proses wajib diisi.';
    if (!form.steps.length) return 'Minimal harus ada satu step produksi.';
    if (form.steps.some(step => !step.nama_step.trim())) return 'Nama pada setiap step wajib diisi.';
    if (form.steps.some(step => Number(step.harga_borongan) < 0)) return 'Harga borongan tidak boleh negatif.';
    return '';
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const validation = validate();
    if (validation) { setError(validation); return; }
    setError('');
    setSaving(true);
    const payload = {
      nama_proses: form.nama_proses.trim(),
      keterangan: form.keterangan,
      is_aktif: form.is_aktif,
      steps: form.steps.map((step, index) => ({
        urutan: index + 1,
        nama_step: step.nama_step.trim(),
        harga_borongan: Number(step.harga_borongan),
      })),
    };
    try {
      const path = mode === 'add' ? '/production/proses-borongan/add' : `/production/proses-borongan/edit/${id}`;
      const { data } = await api.post(path, payload);
      if (data?.success === false) throw new Error(data.message || 'Data gagal disimpan.');
      await Swal.fire({ icon: 'success', title: 'Berhasil', text: data?.message || 'Proses produksi berhasil disimpan.', timer: 1400, showConfirmButton: false });
      navigate('/mkdd/proses-produksi');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="proses-page">
      <Skeleton width={220} height={38} />
      <Card className="proses-card"><Skeleton variant="rounded" height={430} /></Card>
    </div>
  );

  return (
    <div className="proses-page">
      <Button startIcon={<Icon icon="mdi:arrow-left" />} onClick={() => navigate('/mkdd/proses-produksi')}>Kembali</Button>
      <Box className="proses-heading">
        <Box><Typography variant="h4" fontWeight={750}>{mode === 'add' ? 'Tambah Proses' : 'Edit Proses'}</Typography><Typography color="text.secondary">Susun alur dan harga borongan produksi.</Typography></Box>
        <Chip icon={<Icon icon={mode === 'add' ? 'mdi:plus-circle-outline' : 'mdi:pencil-outline'} />} label={mode === 'add' ? 'Data baru' : 'Edit data'} color="primary" variant="outlined" />
      </Box>

      <form onSubmit={submit}>
        <Card className="proses-card">
          <Typography variant="h6" fontWeight={700}>Informasi utama</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>Identitas proses borongan yang akan digunakan.</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Grid container spacing={2.5}>
            <Grid item xs={12}><TextField required fullWidth label="Nama proses" placeholder="Proses Produksi Tas" value={form.nama_proses} onChange={e => setField('nama_proses', e.target.value)} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Keterangan" placeholder="Keterangan singkat proses..." value={form.keterangan || ''} onChange={e => setField('keterangan', e.target.value)} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={form.is_aktif} onChange={e => setField('is_aktif', e.target.checked)} />} label={form.is_aktif ? 'Proses aktif' : 'Proses nonaktif'} /></Grid>
          </Grid>
        </Card>

        <Card className="proses-card">
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
            <Box><Typography variant="h6" fontWeight={700}>Urutan step</Typography><Typography variant="body2" color="text.secondary">Step baru otomatis ditempatkan sebelum BHP. BHP selalu menjadi step terakhir.</Typography></Box>
            <Button variant="outlined" startIcon={<Icon icon="mdi:plus" />} onClick={addStep}>Tambah step</Button>
          </Box>
          <Divider sx={{ mb: 2.5 }} />
          <Stack spacing={2}>
            {form.steps.map((step, index) => (
              <Box className={`step-row ${isBhpStep(step) ? 'fixed-last-step' : ''}`} key={index}>
                <Box className="step-number">{index + 1}</Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={6}><TextField required fullWidth size="small" label="Nama step" value={step.nama_step} disabled={isBhpStep(step)} onChange={e => setStep(index, 'nama_step', e.target.value)} InputProps={isBhpStep(step) ? { endAdornment: <InputAdornment position="end"><Chip size="small" label="Terakhir" color="primary" variant="outlined" /></InputAdornment> } : undefined} /></Grid>
                  <Grid item xs={10} sm={5} md={5.5}><TextField required fullWidth size="small" type="number" label="Harga borongan" inputProps={{ min: 0 }} InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }} value={step.harga_borongan} onChange={e => setStep(index, 'harga_borongan', Number(e.target.value))} /></Grid>
                  <Grid item xs={2} sm={1} md={0.5}><IconButton aria-label={isBhpStep(step) ? 'BHP harus menjadi step terakhir' : 'Hapus step'} color="error" disabled={form.steps.length === 1 || isBhpStep(step)} onClick={() => removeStep(index)}><Icon icon={isBhpStep(step) ? 'mdi:lock-outline' : 'mdi:trash-can-outline'} /></IconButton></Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button size="large" onClick={() => navigate('/mkdd/proses-produksi')}>Batal</Button>
          <Button size="large" type="submit" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Icon icon="mdi:content-save-outline" />}>{saving ? 'Menyimpan...' : 'Simpan proses'}</Button>
        </Box>
      </form>
    </div>
  );
}
